import { FC, useCallback } from 'react';
import { Button, Flex } from 'antd';
import { Modal } from '../../common/components/modal/Modal';
import { ModalFooter } from '../../common/components/modal/ModalFooter';
import { ModalHeader } from '../../common/components/modal/ModalHeader';
import { ModalTitle } from '../../common/components/modal/ModalTitle';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  confirmPanelFormClose,
  Form,
  FormAlerts,
  FormField,
  useAlertContext,
} from '../../common/form';
import { Padding } from '../../common/components/Padding';
import { GetApiErrorMessage } from '../../common/utils/ValidationMessagesMap';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useMutationErrors } from '../../common/queryClient';
import { confirmationHeaders, excelTranslations } from '../../common/utils';
import validationTranslations from '../translations/validation.translations.json';
import { ActionButton } from '../../common/components';
import { DataObjectFormAutoComplete } from '../DataObjectFormAutoComplete';
import { useGetDataObjectDescriptionVersions } from '../query/useGetDataObjectDescriptionVersions';
import { DataObjectVersionSelect } from '../DataObjectVersionSelect';
import { CloneAlgorithmsValidFormDataSchema } from './schema/CloneAlgorithmsValidFormDataSchema';
import translations from './CloneAlgorithmsModal.translations.json';
import {
  CloneAlgorithmsFormDataType,
  CloneAlgorithmsValidFormDataType,
} from './schema/types';
import { useMutateCloneAlgorithmsQuery } from './query/useMutateCloneAlgorithmsQuery';
import { DataObjectDescriptionVersionStore } from '../DataObjectDescriptionVersionStore';
import { ApiErrorResponse } from '../../common/ApiClient';
import { keepPreviousData } from '@tanstack/react-query';
import { AlgorithmBasicType } from '../algorithms/schema/types';
import { useRefetchListAlgorithms } from '../algorithms/query/useGetAlgorithmsQuery';

const { addTranslations, t } = i18nNsFactory(
  'data-object-clone-algorithms-modal',
);
addTranslations(translations);
addTranslations(excelTranslations);
addTranslations(validationTranslations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export interface DataObjectCloneAlgorithmsModalProps {
  onSuccess?: (data: AlgorithmBasicType[]) => void;
  onClose?: () => void;
}

const defaultValues = {
  dataObjectId: undefined,
  versionId: undefined,
};

export const CloneAlgorithmsModal: FC<DataObjectCloneAlgorithmsModalProps> = (
  props,
) => {
  const { onSuccess, onClose } = props;

  const [{ versionId }] = DataObjectDescriptionVersionStore.useValue(
    'dataObjectDescriptionVersion',
  );

  const { mutateAsync } = useMutateCloneAlgorithmsQuery();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { resetAlert, setSuccess } = useAlertContext();
  const refetchAlgorithms = useRefetchListAlgorithms();

  const getErrorMessage: GetApiErrorMessage = useCallback(
    (code, meta) =>
      t(`alert.error.code.${code}`, meta?.args && { ...meta.args }),
    [],
  );
  const handleError = useCallback(
    (error: ApiErrorResponse) => {
      setMutationErrors?.(error.response?.data.errors ?? [], getErrorMessage);
    },
    [getErrorMessage, setMutationErrors],
  );

  const handleSubmit = useCallback(
    (values: CloneAlgorithmsValidFormDataType, confirm: boolean) => {
      resetAlert();
      clearMutationErrors();
      return mutateAsync({
        id: versionId,
        data: values,
        headers: confirmationHeaders(confirm),
      })
        .then(async (data) => {
          await refetchAlgorithms();
          onSuccess?.(data ?? []);
          setSuccess(t('alert.success.cloned'));
        })
        .catch(handleError);
    },
    [
      resetAlert,
      clearMutationErrors,
      mutateAsync,
      versionId,
      handleError,
      onSuccess,
      setSuccess,
    ],
  );

  return (
    <Form
      schema={CloneAlgorithmsValidFormDataSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      serverErrors={mutationErrors}
      idAttributePrefix="cloneAlgorithms"
    >
      <DataObjectCloneAlgorithmsModalInner onClose={onClose} />
    </Form>
  );
};

function DataObjectCloneAlgorithmsModalInner({
  onClose,
}: {
  onClose?: () => void;
}) {
  const { setValue } = useFormContext<CloneAlgorithmsFormDataType>();
  const [dataObjectId, versionId] = useWatch<CloneAlgorithmsValidFormDataType>({
    name: ['dataObjectId', 'versionId'],
  });

  const { isDirty } = useFormState();
  const handleCancel = useCallback(() => {
    if (isDirty) {
      confirmPanelFormClose(onClose);
    } else {
      onClose?.();
    }
  }, [isDirty, onClose]);

  const { data: versions } = useGetDataObjectDescriptionVersions(
    { id: dataObjectId ?? 0 },
    {
      enabled: !!dataObjectId,
      placeholderData: keepPreviousData,
    },
  );

  const handleDataObjectChange = useCallback(() => {
    setValue('versionId', null, { shouldDirty: true });
  }, [setValue]);

  const handleVersionChange = useCallback(
    (versionId?: any) => {
      setValue('versionId', versionId, { shouldDirty: true });
    },
    [setValue],
  );

  return (
    <Modal
      footer={
        <ModalFooter>
          <Flex gap="small" justify="end">
            <Button id="close-clone-algorithms-modal" onClick={handleCancel}>
              {t('button.cancel')}
            </Button>
            <ActionButton type="formSubmit" />
          </Flex>
        </ModalFooter>
      }
      onCancel={onClose}
      width="30rem"
    >
      <ModalHeader>
        <ModalTitle>{t(`title`)}</ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <Flex vertical gap="large">
          <FormField
            label={t('fields.dataObject.label')}
            name={'dataObjectId'}
            required
          >
            <DataObjectFormAutoComplete
              name="dataObjectId"
              placeholder={t('fields.dataObject.placeholder')}
              onSelect={handleDataObjectChange}
            />
          </FormField>
          <FormField
            label={t('fields.version.label')}
            name={'versionId'}
            required
          >
            <DataObjectVersionSelect
              versions={versions || []}
              activeVersionId={versionId}
              onChange={handleVersionChange}
              style={{ width: '50%' }}
            />
          </FormField>
        </Flex>
      </Padding>
    </Modal>
  );
}
